import React, { useCallback } from 'react'
import { Keyboard, StyleSheet, TouchableOpacity } from 'react-native'
import { DS } from '../../design-system'
import { observer } from 'mobx-react-lite'
import { BottomSheetView } from '@gorhom/bottom-sheet'
import { translate } from '../../i18n'
import { events } from '../../models/analytics/analytics-events'
import { GameModeTag, TournamentAccessControl } from '../../models/api-store'
import { getRouteToLastVisitedTab } from '../../navigation'
import { WebWrapper } from '@/components/WebWrapper'
import { useThrottledCallback } from 'use-debounce'
import { useFocusEffect, useRouter } from 'expo-router'
import { useStore } from '@/models'
import { useCreateGame } from '@/hooks/use-create-game'

interface GameCardProps {
  title: string
  bullets: string[]
  onPress: () => void
}

const GameCard = ({ title, bullets, onPress }: GameCardProps) => {
  const onPressDebounce = useThrottledCallback(onPress, 1000)

  return (
    <DS.Frame style={gameCardStyles.container}>
      <DS.Text variant="Heading 3/Medium">{title}</DS.Text>
      <DS.Frame style={gameCardStyles.body}>
        <DS.Frame>
          {bullets.map((bullet, index) => (
            <DS.Text key={index} variant="Body Small/Regular">
              • {bullet}
            </DS.Text>
          ))}
        </DS.Frame>
        <DS.Frame style={gameCardStyles.arrowContainer}>
          <TouchableOpacity onPress={onPressDebounce} style={gameCardStyles.arrow}>
            <DS.Icon name={'Arrow---right'} size={24} color={'Global/White'} />
          </TouchableOpacity>
        </DS.Frame>
      </DS.Frame>
    </DS.Frame>
  )
}

const gameCardStyles = StyleSheet.create({
  container: {
    gap: 4,
    backgroundColor: DS.palette['Global/White'],
    padding: 16,
    width: '100%',
    borderRadius: 16,
    // iOS shadow
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    // Android shadow
    elevation: 8
  },
  body: {
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  arrowContainer: {
    justifyContent: 'center'
  },
  arrow: {
    gap: 8,
    backgroundColor: 'black',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 96,
    width: 48,
    height: 48
  }
})

const JoinLiveGameSheetContent = observer(() => {
  const router = useRouter()
  const {
    communication: { clearChannel },
    game: { clearCurrentTournament },
    analytics: { setEvent }
  } = useStore()

  const navToLandingScreen = useCallback(
    (gameMode: GameModeTag) => {
      const event =
        gameMode === 'default'
          ? events.joinLiveScreen.join_quick_game_tapped
          : events.joinLiveScreen.join_open_tournament_tapped
      setEvent(event)
      clearChannel()
      clearCurrentTournament()
      router.replace(`/game/join/open-table/${gameMode}`)
    },
    [clearChannel, clearCurrentTournament, router]
  )

  return (
    <BottomSheetView style={[styles.container, { paddingBottom: 32 }]}>
      <GameCard
        title={translate('joinLiveGameScreen.quickGame.title')}
        bullets={['Casual play', 'Fixed bets', 'Come and go freely']}
        onPress={() => navToLandingScreen(GameModeTag.default)}
      />
      <GameCard
        title={translate('joinLiveGameScreen.tournament.title')}
        bullets={['Win it all', 'Betting levels increase', 'No rebuys - last woman standing']}
        onPress={() => navToLandingScreen(GameModeTag.tournament)}
      />
    </BottomSheetView>
  )
})

export default JoinLiveGameSheetContent

const styles = StyleSheet.create({
  container: {
    // flex: 1,
    paddingHorizontal: 24,
    gap: 16,
    alignItems: 'center',
    backgroundColor: DS.palette['Brand/Cream']
  }
})
